import { useCallback } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { useDictionaryStore } from "@/stores/dictionaryStore";

interface ClickableWordProps {
  word: string;
  isActive?: boolean;
  onClick?: (word: string) => void;
  className?: string;
}

export function ClickableWord({
  word,
  isActive = false,
  onClick,
  className,
}: ClickableWordProps) {
  const { openPopup } = useDictionaryStore();

  // Kelimeyi temizle
  const cleanWord = word
    .replace(/^[.,!?;:'"()\[\]{}]+|[.,!?;:'"()\[\]{}]+$/g, "")
    .trim();
  const isClickable = cleanWord.length > 0;

  const handleClick = useCallback(() => {
    if (!isClickable) return;

    if (onClick) {
      onClick(cleanWord);
    }

    // Tıklanınca popup'ı aç
    openPopup(cleanWord);
  }, [cleanWord, isClickable, onClick, openPopup]);

  return (
    <motion.span
      className={cn(
        "inline cursor-pointer transition-all duration-150 relative hover:text-accent-purple",
        isActive && "text-accent-blue bg-accent-blue/20 rounded px-0.5",
        className
      )}
      onClick={handleClick}
      whileTap={isClickable ? { scale: 0.95 } : undefined}
    >
      {word}
    </motion.span>
  );
}

interface ClickableTextProps {
  text: string;
  activeWordIndex?: number | null;
  onWordClick?: (word: string, index: number) => void;
  className?: string;
}

export function ClickableText({
  text,
  activeWordIndex,
  onWordClick,
  className,
}: ClickableTextProps) {
  // Metni kelimelere ayır (boşlukları koru)
  const words = text.split(/(\s+)/);

  let wordIndex = 0;

  return (
    <span className={className}>
      {words.map((segment, i) => {
        // Boşluk ise olduğu gibi göster
        if (/^\s+$/.test(segment)) {
          return <span key={i}>{segment}</span>;
        }

        const currentIndex = wordIndex;
        wordIndex++;

        return (
          <ClickableWord
            key={i}
            word={segment}
            isActive={currentIndex === activeWordIndex}
            onClick={(word) => onWordClick?.(word, currentIndex)}
          />
        );
      })}
    </span>
  );
}
